import { request } from '@mairistem/tools';
import React from 'react';
import { toast } from 'react-toastify';

import { ARRETES, REGISSEUR } from '@/constants';
import { postArrete, postArreteAutomatique } from '@/routes';
import type { ArreteType } from '@/types';
import { estEncaissementDirect } from '@/utils';
import { getHttpStatus } from '@/utils/apiErrors';
import { useRegieStore, useRegisseurStore } from '@/zustand';

type UseArreteJournalierAutomatiqueOptions = {
  /**
   * `once` : ne déclenche l'appel qu'une seule fois par régie (verrou module-level partagé).
   * `force` : refire même si la régie a déjà été traitée (n'inspecte ni ne modifie le verrou).
   */
  mode?: 'once' | 'force';
  /**
   * Période couverte par l'arrêté journalier créé :
   * `derniereActivite` : `POST /arretes/automatique` — le back se cale sur le jour de la dernière
   *   activité, plafonné à la veille : la journée en cours n'est JAMAIS arrêtée.
   * `jourEnCours` : `POST /arretes` — le back arrête la journée du jour (`arr_date = now()`),
   *   donc les reçus créés/annulés aujourd'hui sont intégrés.
   */
  portee?: 'derniereActivite' | 'jourEnCours';
};

type UseArreteJournalierAutomatiqueResult = {
  /** Arrêté créé, rempli uniquement en cas de succès ET pour un non-agent (pilote la modale). */
  arreteCreated: ArreteType | null;
  clearArrete: () => void;
  creating: boolean;
  /** Déclenche l'arrêté journalier automatique. Résout après complétion (succès / échec / 422). */
  run: () => Promise<boolean>;
};

// Persiste entre les remounts du composant (remount à chaque changement de page par le framework).
// Réinitialisé uniquement au reload complet de la page. Partagé par toutes les instances du hook.
let lastCreatedIdentifiant: number | null = null;

/**
 * useArreteJournalierAutomatique
 *
 * Encapsule la création d'un arrêté journalier `{ type: 'Journalier' }` — `POST /arretes/automatique`
 * (défaut) ou `POST /arretes` selon `portee` : gestion du 422 silencieux
 * (rien à arrêter), toast d'erreur sinon, et exposition de l'arrêté créé pour affichage d'une
 * modale de confirmation (régisseur uniquement, jamais les agents de guichet).
 *
 * Pour un agent de guichet, le processus est entièrement silencieux : ni modale, ni toast, même en
 * cas d'erreur ≠ 422. `POST /arretes/automatique` lui est ouvert côté api-regie (habilitation 2)
 * afin que le dernier arrêté journalier soit bien créé à sa connexion.
 *
 * Hook impératif : l'appel n'est pas déclenché automatiquement, c'est `run()` qui le lance, ce qui
 * permet à l'appelant de `await run()` avant d'enchaîner (ex : charger le prévisionnel périodique).
 */
const useArreteJournalierAutomatique = (
  options: UseArreteJournalierAutomatiqueOptions = {},
): UseArreteJournalierAutomatiqueResult => {
  const { mode = 'once', portee = 'derniereActivite' } = options;

  const regie = useRegieStore((state) => state.regie);
  const connectedRegisseur = useRegisseurStore((state) => state.regisseur);
  const { mandat } = connectedRegisseur || {};

  const isAgent = mandat === REGISSEUR.TYPE_AGENT;

  const [arreteCreated, setArreteCreated] = React.useState<ArreteType | null>(null);
  const [creating, setCreating] = React.useState<boolean>(false);

  const run = React.useCallback(async (): Promise<boolean> => {
    const identifiant = regie?.identifiant;
    if (!identifiant) return false;
    // En mode embedded (encaissement direct), on ne déclenche pas l'arrêté automatique :
    // l'utilisateur traite un seul encaissement et n'a pas à voir la modale d'arrêté journalier.
    if (estEncaissementDirect()) return false;

    if (mode === 'once') {
      if (lastCreatedIdentifiant === identifiant) return false;
      // Positionné AVANT l'await pour éviter un double appel concurrent sur la même régie.
      lastCreatedIdentifiant = identifiant;
    }

    setCreating(true);
    try {
      const url = portee === 'jourEnCours' ? postArrete() : postArreteAutomatique();

      const response: any = await request.post(url, {
        type: ARRETES.TYPE_JOURNALIER,
      });

      const arrete = response?.data || response;

      if (arrete?.identifiant && !isAgent) {
        setArreteCreated(arrete);
      }

      return true;
    } catch (error: any) {
      // Pour un agent de guichet, le processus doit rester totalement silencieux : la création est
      // déclenchée à la connexion sans qu'il ait à en connaître l'issue (ni modale, ni toast).
      if (!isAgent && getHttpStatus(error) !== 422) {
        toast.error("Une erreur est survenue lors de la création de l'arrêté journalier");
      }
      return false;
    } finally {
      setCreating(false);
    }
  }, [regie?.identifiant, isAgent, mode, portee]);

  const clearArrete = React.useCallback(() => setArreteCreated(null), []);

  return { arreteCreated, clearArrete, creating, run };
};

export default useArreteJournalierAutomatique;
